import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Link2 } from 'lucide-react'
import { Button } from '../../ui/Button/Button'
import { useAuth } from '../auth/AuthProvider'
import panel from '../settings/SettingsSection.module.css'
import type { OnboardingApiClient } from './onboarding-api'
import type { ClinicAdoption } from './organization-onboarding-contracts'
import styles from './OrganizationClinicOnboardingPage.module.css'

export function adoptionPrerequisite(status: string) {
  const labels: Record<string, string> = {
    BOOTSTRAPPING: 'Complete clinic identity setup',
    EMAIL_VERIFICATION_REQUIRED: 'Verify the clinic owner account',
    PROFILE_REQUIRED: 'Complete the clinic profile',
    CRM_CREDENTIAL_REQUIRED: 'Complete NexHealth connection setup',
    CRM_DISCOVERING: 'Complete NexHealth discovery',
    LOCATION_REQUIRED: 'Verify and select the clinic location',
    SCHEDULING_REQUIRED: 'Complete scheduling setup',
    COMMUNICATIONS_REQUIRED: 'Complete Communications preferences',
    REVIEW_REQUIRED: 'Review the existing onboarding configuration',
    PROVISIONING: 'Review provisioning progress',
    VALIDATING: 'Review validation before any activation',
    ACTION_REQUIRED: 'Resolve the existing onboarding action',
    OPERATOR_REQUIRED: 'Request operator review',
    FAILED: 'Review the existing onboarding failure',
    READY: 'Clinic onboarding is complete',
  }
  return labels[status] ?? 'Review the existing onboarding state'
}

/**
 * Link a clinic that already exists in Flio, from an operator-reviewed
 * approval reference. The server preview names the clinic and the
 * administrators who receive access; nothing changes until it is confirmed.
 */
export function ExistingClinicAdoption({
  apiClient,
  onLinked,
}: {
  apiClient: OnboardingApiClient
  /** The server linked this clinic. The page makes it appear in the app. */
  onLinked: (tenantUid: string) => void
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const id = useId()
  const [open, setOpen] = useState(false)
  const [operationId, setOperationId] = useState('')
  const [review, setReview] = useState<ClinicAdoption | null>(null)
  const [result, setResult] = useState<ClinicAdoption | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const preview = useMutation({
    mutationFn: () =>
      apiClient.previewClinicAdoption!(user!, operationId.trim()),
    onSuccess: (value) => {
      setReview(value)
      setConfirmed(false)
    },
  })
  const adopt = useMutation({
    mutationFn: () =>
      apiClient.adoptExistingClinic!(
        user!,
        review!.operationId,
        review!.approvalFingerprint
      ),
    onSuccess: (value) => {
      setResult(value)
      onLinked(value.tenantUid)
    },
    onError: () => {
      setReview(null)
      setConfirmed(false)
    },
  })
  if (result)
    return (
      <section
        className={`${panel.panel} ${styles.adoption}`}
        aria-label="Existing clinic linked"
      >
        <div className={styles.created}>
          <CheckCircle2
            size={22}
            aria-hidden="true"
            className={styles.createdIcon}
          />
          <div>
            <h2>
              {result.clinicName} is linked to {result.organizationName}
            </h2>
            <p>
              Next prerequisite: {adoptionPrerequisite(result.onboardingStatus)}
              .
            </p>
            <p>
              Existing onboarding state: {result.onboardingStatus}. Adoption has
              not completed this prerequisite. Continue it through the clinic’s
              authorized onboarding owner.
            </p>
          </div>
        </div>
      </section>
    )
  return (
    <section
      className={`${panel.panel} ${styles.adoption}`}
      aria-label="Add existing clinic"
    >
      <div className={styles.adoptionHeader}>
        <span className={styles.adoptionIcon} aria-hidden="true">
          <Link2 size={16} />
        </span>
        <div>
          <h2>Linking a clinic that’s already in Flio?</h2>
          <p>Use the approval reference Flio gave you.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          aria-expanded={open}
          aria-controls={`${id}-adoption`}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Close' : 'Link existing clinic'}
        </Button>
      </div>
      {open && (
        <div id={`${id}-adoption`} className={styles.adoptionBody}>
          <div className={styles.fieldGroup}>
            <label className={panel.field} htmlFor={`${id}-reference`}>
              <span>Adoption approval reference</span>
              <input
                id={`${id}-reference`}
                aria-describedby={`${id}-reference-hint`}
                value={operationId}
                autoComplete="off"
                spellCheck={false}
                disabled={adopt.isPending || preview.isPending}
                onChange={(event) => {
                  setOperationId(event.target.value)
                  setReview(null)
                  setConfirmed(false)
                }}
              />
            </label>
            <p id={`${id}-reference-hint`} className={panel.hint}>
              The approval determines the clinic and which administrators get
              access to it.
            </p>
          </div>
          <div className={styles.footer}>
            <Button
              type="button"
              variant="secondary"
              disabled={
                !user ||
                !operationId.trim() ||
                preview.isPending ||
                adopt.isPending
              }
              onClick={() => {
                setReview(null)
                preview.mutate()
              }}
            >
              {preview.isPending ? 'Checking…' : 'Review existing clinic'}
            </Button>
          </div>
          {(preview.isError || adopt.isError) && (
            <p className={panel.fieldError} role="alert">
              <AlertTriangle size={14} aria-hidden="true" />
              This adoption could not be authorized. Request a current reviewed
              approval.
            </p>
          )}
          {review && (
            <div className={styles.location}>
              <div>
                <h3>{review.clinicName}</h3>
                <p>Organization: {review.organizationName}</p>
              </div>
              <dl className={styles.facts}>
                <div>
                  <dt>Clinic ID</dt>
                  <dd className={styles.mono}>{review.tenantUid}</dd>
                </div>
                <div>
                  <dt>Current prerequisite</dt>
                  <dd>{adoptionPrerequisite(review.onboardingStatus)}</dd>
                </div>
                <div className={styles.factWide}>
                  <dt>Administrators receiving clinic access</dt>
                  <dd>
                    <ul className={styles.recipients}>
                      {review.recipientUids.map((uid) => (
                        <li key={uid} className={styles.mono}>
                          {uid}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              </dl>
              <label className={styles.confirm}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                I confirm linking this clinic and granting the listed
                administrator access.
              </label>
              <div className={styles.footer}>
                <Button
                  type="button"
                  disabled={!confirmed || adopt.isPending || !user}
                  onClick={() => adopt.mutate()}
                >
                  {adopt.isPending ? 'Linking…' : 'Link clinic'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
